import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ChartData, ChartConfiguration, ChartType } from 'chart.js';
import { ExerciseHistory } from 'src/models/exercise.model';
import { PlanService } from 'src/services/plan.service';

@Component({
    selector: 'app-detail-chart',
    templateUrl: './detail-chart.component.html',
    styleUrls: ['./detail-chart.component.scss'],
    standalone: false
})
export class DetailChartComponent implements OnInit, OnChanges {
  @Input() exerciseHistories: ExerciseHistory[] = [];

  public lineChartData!: ChartData<'line'>;
  public lineChartOptions: ChartConfiguration['options'];
  public lineChartType: ChartType = 'line';

  public distinctPlanIds: string[] = [];
  public planNamesMap: Map<string, string> = new Map(); // Stores plan names by ID
  public selectedPlanId: string = ''; // Default to empty for "All"

  constructor(private planService: PlanService) {}

  ngOnInit() {
    this.extractDistinctPlanIds();
    this.fetchPlanNames(); // Fetch plan names on init
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['exerciseHistories']) {
      this.extractDistinctPlanIds();
      this.fetchPlanNames(); // Fetch plan names when exercise histories change
      this.prepareChartData();
    }
  }

  // Extracts distinct plan IDs with more than one occurrence from exerciseHistories
  extractDistinctPlanIds() {
    const planIdCount: { [key: string]: number } = {};

    this.exerciseHistories.forEach((history) => {
      // Workouts started without a saved plan deliberately have an empty
      // activePlanId.  It is not a Firestore document id and must never be
      // passed to `doc(firestore, 'workoutPlans', id)`.
      const planId = history.activePlanId?.trim();
      if (!planId || planId.includes('/')) return;
      planIdCount[planId] = (planIdCount[planId] || 0) + 1;
    });

    // Filter out plan IDs that have more than one occurrence
    this.distinctPlanIds = Object.keys(planIdCount).filter(
      (planId) => planIdCount[planId] > 1
    );
  }

  // Fetch plan names by their IDs
  async fetchPlanNames() {
    try {
      if (this.distinctPlanIds.length > 0) {
        this.planNamesMap = await this.planService.getPlanNamesByIds(
          this.distinctPlanIds
        );
      }
    } catch (error) {
      console.error('Failed to fetch plan names:', error);
    }
  }

  // Filters data based on the selected plan ID
  filterDataByPlan() {
    // If no planId is selected (""), display all data
    const filteredHistories = this.selectedPlanId
      ? this.exerciseHistories.filter(
          (history) => history.activePlanId === this.selectedPlanId
        )
      : this.exerciseHistories;

    this.prepareChartData(filteredHistories);
  }

  // Prepares chart data
  prepareChartData(histories: ExerciseHistory[] = this.exerciseHistories) {
    const labels = histories
      .map((history) =>
        new Date(history.createdAt?.toDate()!).toLocaleDateString()
      )
      .reverse();

    const data = histories
      .map((history) => {
        const totalWeight = history.weights.reduce((acc, cur) => acc + cur, 0);
        return history.weights.length
          ? totalWeight / history.weights.length
          : 0;
      })
      .reverse();

    this.lineChartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          label: 'Average Weight',
          backgroundColor: 'rgba(148,159,177,0.2)',
          borderColor: 'rgba(148,159,177,1)',
          pointBackgroundColor: 'rgba(148,159,177,1)',
          pointBorderColor: '#fff',
          fill: 'origin',
        },
      ],
    };

    this.lineChartOptions = {
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    };
  }

  // Returns the plan name by ID from the fetched plan names map
  getPlanName(planId: string): string {
    return this.planNamesMap.get(planId) || 'Unknown Plan Name';
  }
}
